import React, { PropsWithChildren, useEffect } from "react";

import { DateRange } from "@performyard/py-components";

import PageTitle from "../../../../components/PageTitle";
import Text from "../../../../components/Text";

import useStore from "../../store";

import { useCompanyBasic, usePageSettings, useUpdatePageSettings } from "../../../../api/hooks";

import Config from "../../Config";
import { IEngagementCustomFilterValue, IEngagementDefaultFilterValue } from "../../schema";
import DatePickerMenu, { SelectionOption } from "../DatePickerMenu";
import Navigation from "../Navigation";
import css from "./Layout.module.scss";

interface BaseProps {
	title: string;
	topAreaMaxHeight?: number;
}

/**
 *  top-level filter with state shared between all pages within engagement section
 */
const Filter = () => {
	// setup
	const [filter, setFilter] = useStore((state) => [state.filter, state.setFilter]);

	// update page settings hook
	const { mutate: updatePageSettings } = useUpdatePageSettings();

	// uses onSuccess hook to set initial page settings
	const { data: pageSettingsData, isLoading, isSuccess } = usePageSettings();

	useEffect(() => {
		if (isSuccess && pageSettingsData?.dateRange && pageSettingsData?.dateRange !== filter.value) {
			const dateFilterType = typeof pageSettingsData?.dateRange === "string" ? "default" : "custom";
			setFilter(dateFilterType, pageSettingsData.dateRange);
		}
	}, [isLoading]);

	// events
	const handleFilterChange = (type: "default" | "custom", value: SelectionOption | DateRange) => {
		if (type === "default") {
			const defaultValue = value as SelectionOption;
			if (defaultValue.value !== filter.value) {
				setFilter(type, defaultValue.value as IEngagementDefaultFilterValue);
				updatePageSettings({ settings_path: "dateRange", settings_value: defaultValue.value });
			}
		}
		if (type === "custom") {
			const customValue = value as IEngagementCustomFilterValue;
			setFilter(type, customValue);
			updatePageSettings({ settings_path: "dateRange", settings_value: customValue });
		}
	};

	// render
	return (
		<DatePickerMenu
			options={Config.FILTER_OPTIONS}
			selected={isLoading ? "" : filter.value}
			handleSelect={handleFilterChange}
		/>
	);
};

/**
 * Renders a layout component with a navigation bar and a section content
 * to be used as a wrapper for all pages in the engagement section
 */
export const Layout = (props: PropsWithChildren<BaseProps>) => {
	const { children, topAreaMaxHeight, title } = props;
	const { company } = useCompanyBasic();

	return (
		<div className={css.engagementDashboard}>
			<PageTitle title={`Engagement ${title} ${company?.name ? `- ${company.name} -` : "-"} PerformYard`} />
			<Navigation />
			<div
				className={css.topArea}
				style={{ maxHeight: topAreaMaxHeight, minHeight: topAreaMaxHeight || "100vh" }}
			>
				<div className={css.innerPage}>
					<div className={css.header}>
						<Text size="heading-5" weight="bold">
							{props.title}
						</Text>
						<div className={css.filter}>
							<Filter />
						</div>
					</div>
					<div className={css.sectionContent}>{children}</div>
				</div>
			</div>
		</div>
	);
};

/**
 * renders a divider that spans the width of the parent element
 */
export const Divider = () => {
	return <div className={css.divider} />;
};

interface RowProps {
	height: number;
	className?: string;
}

/**
 * Renders a row with a fixed height that is the width of the parent element
 */
export const Row = (props: PropsWithChildren<RowProps>) => {
	const { children, height, className = css.row } = props;
	return (
		<div className={className} style={{ height }}>
			{children}
		</div>
	);
};
